import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, Globe, MapPin, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

// -------- Contact details (single source of truth) --------
const WEBSITE = "www.medxai.online";
const EMAIL = "info@medxai.online";
const WHATSAPP_INTL = "+27652476091";
const WHATSAPP_DISPLAY = "+27 65 247 6091";
const SA_ADDRESS_LINE1 = "140a Kelvin Drive";
const SA_ADDRESS_LINE2 = "Morning Glen, Sandton, JHB, South Africa";
const UK_ADDRESS_LINE1 = "71-75 Shelton Street";
const UK_ADDRESS_LINE2 = "London WC2H 9JQ, United Kingdom";

// Google Maps embed URL for the SA address (no API key required for the public embed)
const SA_MAP_SRC = "https://www.google.com/maps?q=140a+Kelvin+Drive,+Morning+Glen,+Sandton,+South+Africa&output=embed";

// SVG inline flags — crisp, no external deps.
function FlagSA({ className = "w-5 h-4" }) {
    return (
        <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_medx-dashboard-demo/artifacts/fpqgpkzi_images.jpg"
            alt="South African flag"
            className={`${className} rounded-sm shadow-sm object-cover`}
            draggable="false"
        />
    );
}

function FlagUK({ className = "w-5 h-4" }) {
    return (
        <svg viewBox="0 0 60 40" className={className} aria-hidden>
            <rect width="60" height="40" fill="#012169" />
            <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="6" />
            <path d="M0,0 L60,40 M60,0 L0,40" stroke="#c8102e" strokeWidth="2.5" />
            <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="10" />
            <path d="M30,0 V40 M0,20 H60" stroke="#c8102e" strokeWidth="6" />
        </svg>
    );
}

export default function Contact() {
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/contact", form);
            toast.success(data.message || t("contact.form.sent"));
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t("contact.form.failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-screen" data-testid="contact-page">
            <Navbar />
            <main className="pt-28">
                <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-4">{t("contact.eyebrow")}</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter max-w-4xl mb-6">
                        {t("contact.titlePre")}<span className="brand-gradient-text">{t("contact.titleAccent")}</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
                        {t("contact.intro")}
                    </p>
                </section>

                <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 pb-10">
                    {/* -------- Left column: contact details -------- */}
                    <div className="lg:col-span-5 space-y-5">
                        <div className="card-soft p-6" data-testid="contact-brand-card">
                            <Globe className="w-6 h-6 text-[var(--medx-orange)] mb-3" />
                            <div className="font-display text-xl font-semibold mb-1">{t("contact.companyTitle")}</div>
                            <p className="text-gray-600 text-sm leading-relaxed">{t("contact.companyDesc")}</p>
                            <a href={`https://${WEBSITE}`} target="_blank" rel="noreferrer"
                               className="text-[var(--medx-red)] text-sm font-semibold mt-2 inline-block hover:underline"
                               data-testid="contact-website">
                                {WEBSITE} →
                            </a>
                        </div>

                        <div className="card-soft p-6 space-y-4" data-testid="contact-details-card">
                            <div className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-[var(--medx-red)] mt-0.5" />
                                <div>
                                    <div className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("contact.emailLabel")}</div>
                                    <a href={`mailto:${EMAIL}`} className="text-gray-900 font-medium hover:text-[var(--medx-red)]" data-testid="contact-email-link">
                                        {EMAIL}
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Phone className="w-5 h-5 text-[var(--medx-red)] mt-0.5" />
                                <div>
                                    <div className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("contact.phoneLabel")}</div>
                                    <a href={`https://wa.me/${WHATSAPP_INTL.replace("+", "")}`} target="_blank" rel="noreferrer"
                                       className="text-gray-900 font-medium hover:text-[var(--medx-red)]" data-testid="contact-whatsapp-link">
                                        {WHATSAPP_DISPLAY}
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* South Africa office */}
                        <div className="card-soft p-6" data-testid="contact-sa-office">
                            <div className="flex items-center gap-3 mb-3">
                                <FlagSA className="w-7 h-5 rounded-sm shadow-sm" />
                                <div className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("contact.headOfficeLabel")}</div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-[var(--medx-red)] mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-gray-900 font-medium">{SA_ADDRESS_LINE1}</div>
                                    <div className="text-gray-700 text-sm">{SA_ADDRESS_LINE2}</div>
                                </div>
                            </div>
                        </div>

                        {/* UK office */}
                        <div className="card-soft p-6" data-testid="contact-uk-office">
                            <div className="flex items-center gap-3 mb-3">
                                <FlagUK className="w-7 h-5 rounded-sm shadow-sm" />
                                <div className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("contact.ukOfficeLabel")}</div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-[var(--medx-orange)] mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-gray-900 font-medium">{UK_ADDRESS_LINE1}</div>
                                    <div className="text-gray-700 text-sm">{UK_ADDRESS_LINE2}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* -------- Right column: form -------- */}
                    <div className="lg:col-span-7">
                        <form onSubmit={submit} className="card-soft p-7 md:p-9 space-y-4" data-testid="contact-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text" placeholder={t("contact.form.name")} required
                                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    data-testid="contact-name"
                                />
                                <input
                                    type="email" placeholder={t("contact.form.email")} required
                                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    data-testid="contact-email"
                                />
                            </div>
                            <input
                                type="text" placeholder={t("contact.form.subject")} required
                                value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                data-testid="contact-subject"
                            />
                            <textarea
                                rows={6} placeholder={t("contact.form.message")} required
                                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                                data-testid="contact-message"
                            />
                            <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="contact-submit">
                                {loading ? t("contact.form.sending") : (<><Send className="w-4 h-4" /> {t("contact.form.send")}</>)}
                            </button>
                            <p className="text-xs text-gray-500 pt-1">
                                Your message is delivered to <span className="font-mono text-gray-700">{EMAIL}</span>
                            </p>
                        </form>
                    </div>
                </section>

                {/* -------- Google Map (full width) -------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20" data-testid="contact-map-section">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3">
                        {t("contact.mapTitle")}
                    </div>
                    <div className="rounded-3xl overflow-hidden border border-orange-100 shadow-sm">
                        <iframe
                            title="Medx AI South Africa Office"
                            src={SA_MAP_SRC}
                            className="w-full h-[420px] border-0"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                            data-testid="contact-google-map"
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
